import { PublishedFormId } from '@furnish-hx/constants/FormId';
import FormTypeId from '@core/constants/FormTypeId';
import {
    PortalLeavesId,
    PortalOpeningId,
    PortalOptionsId,
    PortalStyleId,
    PortalTypeId,
} from '@core/constants/Shape';
import { MaterialId } from '@core/constants/Material';
import { FieldDimensionId } from '@core/constants/FieldDimension';
import fieldPortfolioData from './common/fieldsPortfolioData';
import {
    IFormFieldKey,
    IForm,
    IFormInputType,
    IDimensionUnit,
} from '../../interfaces/Form';
import fieldsContact from './common/fieldsContact';
import additionalDataFactory from './common/fieldsAdditionalData';
import {
    allCompositeWoods,
    allGlasses,
    allMetals,
    allPlastics,
    allSolidWoods,
    getMaterialsWithExceptions,
} from './common/materialGroups';

const portalForm: IForm = {
    formId       : PublishedFormId.Portal,
    type         : FormTypeId.Door,
    thumbnailURL : '/resized-images/forms/portal.jpg',
    fieldsOrder  : [
        IFormFieldKey.PortfolioData,
        IFormFieldKey.Comment,
        IFormFieldKey.PortalShape,
        IFormFieldKey.PortalShapeDimension,
        IFormFieldKey.PortalOpening,
        IFormFieldKey.PortalMotorization,
        IFormFieldKey.Material,
        IFormFieldKey.SecondMaterial,
        IFormFieldKey.PortalFilling,
        IFormFieldKey.PortalGate,
        IFormFieldKey.AddOns,
        IFormFieldKey.PortalLaying,
        IFormFieldKey.AdditionalData,
        IFormFieldKey.Contact,
    ],
    fields: {
        portfolioData : fieldPortfolioData,
        comment       : {
            comment: {
                value    : undefined,
                required : false,
            },
        },
        portalShape: {
            value   : undefined,
            options : [
                PortalTypeId.PortalConcave,
                PortalTypeId.PortalConvex,
                PortalTypeId.PortalDiagonalHight,
                PortalTypeId.PortalDiagonalLow,
                PortalTypeId.PortalPolicemanHat,
                PortalTypeId.PortalPolicemanHatInverted,
                PortalTypeId.PortalStraight,
            ],
            required: true,
        },
        portalShapeDimension: {
            shape: {
                value   : undefined,
                options : [
                    PortalLeavesId.PortalOneLeave,
                    PortalLeavesId.PortalTowLeave,
                ],
                required: true,
            },
            dimension: {
                options: {
                    [PortalLeavesId.PortalOneLeave]: {
                        [FieldDimensionId.Width]: {
                            value        : undefined,
                            type         : IFormInputType.Range,
                            unit         : IDimensionUnit.Centimeters,
                            step         : 100,
                            range        : { min: 1000, max: 4500 },
                            required     : true,
                            defaultValue : 3000,
                        },
                        [FieldDimensionId.Height]: {
                            value        : undefined,
                            type         : IFormInputType.Range,
                            unit         : IDimensionUnit.Centimeters,
                            step         : 10,
                            range        : { min: 800, max: 3000 },
                            required     : true,
                            defaultValue : 1500,
                        },
                    },
                    [PortalLeavesId.PortalTowLeave]: {
                        [FieldDimensionId.Width1]: {
                            value        : undefined,
                            type         : IFormInputType.Range,
                            unit         : IDimensionUnit.Centimeters,
                            step         : 100,
                            range        : { min: 1000, max: 2500 },
                            required     : true,
                            defaultValue : 2000,
                        },
                        [FieldDimensionId.Width2]: {
                            value        : undefined,
                            type         : IFormInputType.Range,
                            unit         : IDimensionUnit.Centimeters,
                            step         : 100,
                            range        : { min: 1000, max: 2500 },
                            required     : true,
                            defaultValue : 2000,
                        },
                        [FieldDimensionId.Height]: {
                            value        : undefined,
                            type         : IFormInputType.Range,
                            unit         : IDimensionUnit.Centimeters,
                            step         : 10,
                            range        : { min: 800, max: 3000 },
                            required     : true,
                            defaultValue : 1500,
                        },
                    },
                },
            },
        },
        portalOpening: {
            value   : undefined,
            options : [
                PortalOpeningId.PortalOpeningSlide,
                PortalOpeningId.PortalOpeningSwing,
            ],
            required: true,
        },
        portalMotorization: {
            value: undefined,
        },
        material: {
            material: {
                value   : undefined,
                options : getMaterialsWithExceptions(
                    [
                        ...allMetals,
                        ...allSolidWoods,
                        ...allCompositeWoods,
                        MaterialId.ThermoHeatedWoodAsh,
                        MaterialId.ThermoHeatedWoodPine,
                        ...allPlastics,
                        ...allGlasses,
                    ],
                    [
                        MaterialId.Rust,
                        MaterialId.Brass,
                        MaterialId.Iron,
                        MaterialId.ThermoLaqueredBlack,
                        MaterialId.ThermoLaqueredWhite,
                        MaterialId.SheetMetal,
                        MaterialId.StainlessSteel,
                        MaterialId.Zinc,
                        MaterialId.OakOld,
                        MaterialId.WagonfloorDark,
                        MaterialId.WagonfloorLight,
                        MaterialId.Walnut,
                        MaterialId.AshOlive,
                        MaterialId.Acacia,
                        MaterialId.Mahogany,
                        MaterialId.Elm,
                        MaterialId.Alderwood,
                        MaterialId.Birch,
                        MaterialId.Maple,
                        MaterialId.Teak,
                        MaterialId.Wenge,
                        MaterialId.Cherrywood,
                        MaterialId.Ebony,
                        MaterialId.Spruce,
                        MaterialId.Rubber,
                        MaterialId.DriftWood,
                        MaterialId.Poplar,
                        MaterialId.Ipe,
                        MaterialId.Padouk,
                        MaterialId.Redcedar,
                        MaterialId.Iroko,
                        MaterialId.BlackLocust,
                        MaterialId.Garapa,
                        MaterialId.PlexiglassFluorescent,
                        MaterialId.PlexiglassFrosted,
                        MaterialId.PlexiglassMirror,
                        MaterialId.PlexiglassTransparent,
                        MaterialId.PlexiglassWhite,
                        MaterialId.PolycarbonateOpaque,
                        MaterialId.PolycarbonateTeinted,
                        MaterialId.PolycarbonateTransparent,
                        MaterialId.PvcRigid,
                        MaterialId.RecycledPlastic,
                    ],
                ),
                required: true,
            },
        },
        secondMaterial: {
            material: {
                value   : undefined,
                options : getMaterialsWithExceptions(
                    [
                        ...allMetals,
                        ...allSolidWoods,
                        ...allCompositeWoods,
                        MaterialId.ThermoHeatedWoodAsh,
                        MaterialId.ThermoHeatedWoodPine,
                        ...allPlastics,
                        ...allGlasses,
                    ],
                    [
                        MaterialId.Rust,
                        MaterialId.Brass,
                        MaterialId.Iron,
                        MaterialId.ThermoLaqueredBlack,
                        MaterialId.ThermoLaqueredWhite,
                        MaterialId.SheetMetal,
                        MaterialId.StainlessSteel,
                        MaterialId.Zinc,
                        MaterialId.OakOld,
                        MaterialId.WagonfloorDark,
                        MaterialId.WagonfloorLight,
                        MaterialId.Walnut,
                        MaterialId.AshOlive,
                        MaterialId.Acacia,
                        MaterialId.Mahogany,
                        MaterialId.Elm,
                        MaterialId.Alderwood,
                        MaterialId.Birch,
                        MaterialId.Maple,
                        MaterialId.Teak,
                        MaterialId.Wenge,
                        MaterialId.Cherrywood,
                        MaterialId.Ebony,
                        MaterialId.Spruce,
                        MaterialId.Rubber,
                        MaterialId.DriftWood,
                        MaterialId.Poplar,
                        MaterialId.Ipe,
                        MaterialId.Padouk,
                        MaterialId.Redcedar,
                        MaterialId.Iroko,
                        MaterialId.BlackLocust,
                        MaterialId.Garapa,
                        MaterialId.PlexiglassFluorescent,
                        MaterialId.PlexiglassFrosted,
                        MaterialId.PlexiglassMirror,
                        MaterialId.PlexiglassTransparent,
                        MaterialId.PlexiglassWhite,
                        MaterialId.PolycarbonateOpaque,
                        MaterialId.PolycarbonateTeinted,
                        MaterialId.PolycarbonateTransparent,
                        MaterialId.PvcRigid,
                        MaterialId.RecycledPlastic,
                    ],
                ),
                required: false,
            },
        },
        portalFilling: {
            value   : undefined,
            options : [
                PortalStyleId.PortalOpenWork,
                PortalStyleId.PortalSolid,
            ],
            required: true,
        },
        portalGate: {
            [FieldDimensionId.Width]: {
                value        : undefined,
                type         : IFormInputType.Range,
                unit         : IDimensionUnit.Centimeters,
                step         : 10,
                range        : { min: 800, max: 2000 },
                required     : false,
                defaultValue : 1000,
            },
            [FieldDimensionId.Height]: {
                value        : undefined,
                type         : IFormInputType.Range,
                unit         : IDimensionUnit.Centimeters,
                step         : 10,
                range        : { min: 800, max: 3000 },
                required     : false,
                defaultValue : 1500,
            },
        },
        addOns: {
            [PortalOptionsId.PortalIntercom]: {
                value : undefined,
                min   : 0,
                max   : 1,
            },
            [PortalOptionsId.PortalVideophone]: {
                value : undefined,
                min   : 0,
                max   : 1,
            },
            [PortalOptionsId.PortalLighting]: {
                value : undefined,
                min   : 0,
                max   : 1,
            },
        },
        portalLaying: {
            value: undefined,
        },
        additionalData: additionalDataFactory({
            budgetMin          : 1000,
            budgetMax          : 8000,
            budgetDefaultValue : 2500,
            hasQuantity        : false,
        }),
        contact: fieldsContact,
    },
};

export default portalForm;
